import { Box, Tooltip } from '@mui/material';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { LeadStageResponse } from '../../kanban-board/services/responses/lead.response';

interface Props {
    stages: LeadStageResponse[];
    currentId: string;
    onMove: (stageId: string) => void;
}

/// Profundidade da ponta de cada seta, e quanto a próxima entra nela.
const TIP = 10;
const OVERLAP = 7;

/// A primeira não tem recorte à esquerda: não há seta antes para encaixar.
const FIRST_SHAPE = `polygon(0 0, calc(100% - ${TIP}px) 0, 100% 50%, calc(100% - ${TIP}px) 100%, 0 100%)`;
const SHAPE = `polygon(0 0, calc(100% - ${TIP}px) 0, 100% 50%, calc(100% - ${TIP}px) 100%, 0 100%, ${TIP}px 50%)`;

/// Onde o lead está no funil e o clique que o move: setas encadeadas; o nome inteiro está na dica.
export const LeadStageBar = ({ stages, currentId, onMove }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const current = stages.findIndex((stage) => stage.id === currentId);

    if (stages.length === 0) return null;

    // Mais etapas, setas mais estreitas: com oito a barra empurraria o resto.
    const width = stages.length <= 4 ? 140 : stages.length <= 6 ? 112 : 88;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {stages.map((stage, index) => {
                const isCurrent = index === current;
                const done = current >= 0 && index < current;

                const background = isCurrent ? cc.accent : done ? `${cc.accent}73` : cc.countBg;
                const text = isCurrent || done ? '#fff' : cc.textSecondary;

                return (
                    <Tooltip
                        key={stage.id}
                        title={isCurrent ? stage.name : t('telaLead.moverPara', { etapa: stage.name })}
                    >
                        <Box
                            role="button"
                            tabIndex={0}
                            aria-current={isCurrent ? 'step' : undefined}
                            onClick={() => !isCurrent && onMove(stage.id)}
                            onKeyDown={(e) => e.key === 'Enter' && !isCurrent && onMove(stage.id)}
                            sx={{
                                width,
                                height: 30,
                                ml: index === 0 ? 0 : `-${OVERLAP}px`,
                                clipPath: index === 0 ? FIRST_SHAPE : SHAPE,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                // O texto fica no miolo: a ponta e o recorte comem as laterais.
                                pl: index === 0 ? 1.25 : `${TIP + 6}px`,
                                pr: `${TIP + 4}px`,
                                bgcolor: background,
                                color: text,
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: isCurrent ? 'default' : 'pointer',
                                transition: 'background-color .15s',
                                '&:hover': isCurrent ? {} : { bgcolor: done ? `${cc.accent}a6` : cc.borderHover },
                            }}
                        >
                            <Box
                                component="span"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {stage.name}
                            </Box>
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
};
